import { Stack, Typography, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Box } from "@mui/material";
import React from "react";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { CircuitCreateStepperData, CircuitCreateStepperDataPackageType, CircuitCreateStepperStockType } from "./circuitCreateStepper";
import { DynamicForm, Groups3, CheckCircle, Inventory, AccountTree, Cancel, HelpOutline } from "@mui/icons-material";
import { allPackageFeatureKeys, getPackageFeatureKeys } from "./utils/circuitPackageFeatures";

interface CircuitCreateStepperPackageTypeProps {
    next: () => void;
    setData: React.Dispatch<React.SetStateAction<CircuitCreateStepperData>>;
    stepIndex: number;
}
const PACKAGE_TYPE_ICON: Record<'WITHOUT_PRODUCT' | 'WITH_PRODUCT' | 'DYNAMIC' | 'SKELETON_PACKAGE' | 'UNKNOWN', JSX.Element> = {
    'WITHOUT_PRODUCT': <Inventory sx={{ fontSize: 40 }} />,
    'WITH_PRODUCT': <Groups3 sx={{ fontSize: 40 }} />,
    'DYNAMIC': <DynamicForm sx={{ fontSize: 40 }} />,
    'SKELETON_PACKAGE': <AccountTree sx={{ fontSize: 40 }} />,
    'UNKNOWN': <HelpOutline sx={{ fontSize: 40 }} />,
};

const PACKAGE_TYPES_TO_SHOW: CircuitCreateStepperDataPackageType[] = [
    'WITHOUT_PRODUCT' as CircuitCreateStepperDataPackageType,
    'WITH_PRODUCT' as CircuitCreateStepperDataPackageType,
    'DYNAMIC' as CircuitCreateStepperDataPackageType,
    'SKELETON_PACKAGE' as CircuitCreateStepperDataPackageType,
    'UNKNOWN' as CircuitCreateStepperDataPackageType,
];

export const CircuitCreateStepperPackageType: FC<CircuitCreateStepperPackageTypeProps> = ({ next, setData }) => {
    const { t } = useTranslation();

    const getPackageFeatures = (package_type: CircuitCreateStepperDataPackageType): Array<{ key: string; label: string; available: boolean }> => {
        // Pas de features pour UNKNOWN
        if (package_type === 'UNKNOWN') {
            return [];
        }

        const baseKey = package_type === 'WITHOUT_PRODUCT'
            ? 'shared.package-without-products-features'
            : package_type === 'WITH_PRODUCT'
                ? 'shared.package-with-products-features'
                : package_type === 'SKELETON_PACKAGE'
                    ? 'shared.package-skeleton-features'
                    : 'shared.package-dynamic-features';

        const packageFeatureKeys = getPackageFeatureKeys(package_type);

        return Array.from(allPackageFeatureKeys).map(key => ({
            key,
            label: t(`${baseKey}.${key}`),
            available: packageFeatureKeys.includes(key)
        }));
    };

    const selectPackageType = (package_type: CircuitCreateStepperDataPackageType) => {
        // Si UNKNOWN, on initialise avec des valeurs par défaut et on laisse l'utilisateur choisir
        if (package_type === 'UNKNOWN') {
            setData(data => ({
                ...data,
                package_type,
                without_products: false,
                forfait_price: false,
                price_mix: false,
            }));
            next();
            return;
        }

        const featureKeys = getPackageFeatureKeys(package_type);

        // Déterminer without_products en fonction des features
        // Si 'no-product' est présent => without_products = true
        // Si 'xml-products' est présent => without_products = false
        const hasNoProduct = featureKeys.includes('no-product');
        const hasXmlProducts = featureKeys.includes('xml-products');
        const without_products = hasNoProduct ? true : hasXmlProducts ? false : false;

        // Déterminer forfait_price et price_mix en fonction des features
        const hasManualPrice = featureKeys.includes('manual-price');
        const hasAutoManualMix = featureKeys.includes('auto-manual-mix');
        const hasAutoPrice = featureKeys.includes('auto-price');

        let forfait_price = false;
        let price_mix = false;

        if (hasAutoManualMix) {
            // Mix calcul automatique / saisie manuelle
            forfait_price = false;
            price_mix = true;
        } else if (hasAutoPrice && !hasManualPrice) {
            // Calcul automatique des prix uniquement
            forfait_price = true;
            price_mix = false;
        } else if (hasManualPrice) {
            // Saisie manuelle des prix par typologie de voyageurs
            forfait_price = false;
            price_mix = false;
        }

        setData(data => ({
            ...data,
            package_type,
            without_products,
            forfait_price,
            price_mix,
            stock_type:
                package_type === 'WITHOUT_PRODUCT'
                    && (data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PRODUCT || data.stock_type === CircuitCreateStepperStockType.STOCK_ON_THE_PRODUCT_AND_PACKAGE)
                    ? CircuitCreateStepperStockType.STOCK_ON_THE_PACKAGE :
                    package_type === 'DYNAMIC' ? CircuitCreateStepperStockType.PACKAGE_WITHOUT_STOCK : data.stock_type,
        }));
        next();
    };

    const getPackageTitle = (package_type: CircuitCreateStepperDataPackageType): string => {
        if (package_type === 'WITHOUT_PRODUCT') return t('shared.package-without-products');
        if (package_type === 'WITH_PRODUCT') return t('shared.package-with-products');
        if (package_type === 'SKELETON_PACKAGE') return t('shared.package-skeleton');
        if (package_type === 'UNKNOWN') return t('shared.package-unknown');
        return t('shared.package-dynamic');
    };

    const getPackageSubtitle = (package_type: CircuitCreateStepperDataPackageType): string => {
        if (package_type === 'WITHOUT_PRODUCT') return t('shared.package-without-products-subtitle');
        if (package_type === 'WITH_PRODUCT') return t('shared.package-with-products-subtitle');
        if (package_type === 'SKELETON_PACKAGE') return t('shared.package-skeleton-subtitle');
        if (package_type === 'UNKNOWN') return t('shared.package-unknown-subtitle');
        return t('shared.package-dynamic-subtitle');
    };

    const getPackageDescription = (package_type: CircuitCreateStepperDataPackageType): string => {
        if (package_type === 'WITHOUT_PRODUCT') return t('shared.package-without-products-description');
        if (package_type === 'WITH_PRODUCT') return t('shared.package-with-products-description');
        if (package_type === 'SKELETON_PACKAGE') return t('shared.package-skeleton-description');
        if (package_type === 'UNKNOWN') return t('shared.package-unknown-description');
        return t('shared.package-dynamic-description');
    };

    return (
        <Stack direction={'column'} justifyContent={'center'} alignItems={'center'} sx={{ width: '100%', gap: 4 }}>
            <Stack direction={'column'} spacing={1} sx={{ maxWidth: 'md', width: '100%' }}>
                <Typography variant="h3" sx={{ color: '#486284', fontWeight: 700 }}>{t('shared.step-number', { count: 1 })} : {t('shared.package-structure-type')}</Typography>
                <Typography variant="subtitle1" sx={{ color: '#8CA2C0', fontWeight: 400 }}>{t('shared.package-type-helper-text')}</Typography>
            </Stack>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'stretch'} sx={{ width: '100%', px: 4 }} spacing={3}>
                {PACKAGE_TYPES_TO_SHOW.map((package_type) => {
                    return (
                        <Card
                            key={`circuit-create-package-type-option-${package_type}`}
                            onClick={() => selectPackageType(package_type)}
                            sx={{
                                flex: 1,
                                cursor: 'pointer',
                                position: 'relative',
                                border: 1,
                                borderColor: 'divider',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    boxShadow: 6,
                                    transform: 'translateY(-4px)',
                                },
                            }}
                        >
                            <CardContent sx={{ pt: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', justifyContent: package_type === 'UNKNOWN' ? 'center' : 'flex-start', alignItems: package_type === 'UNKNOWN' ? 'center' : 'stretch' }}>
                                <Stack direction="column" spacing={2} alignItems="center">
                                    <Box sx={{ color: 'primary.main' }}>
                                        {PACKAGE_TYPE_ICON[package_type as 'WITHOUT_PRODUCT' | 'WITH_PRODUCT' | 'DYNAMIC' | 'SKELETON_PACKAGE' | 'UNKNOWN']}
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>
                                        {getPackageTitle(package_type)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontStyle: 'italic' }}>
                                        {getPackageSubtitle(package_type)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                        {getPackageDescription(package_type)}
                                    </Typography>
                                </Stack>

                                {package_type !== 'UNKNOWN' && <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 'auto' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                        {t('shared.features-available')}
                                    </Typography>
                                    <List dense disablePadding>
                                        {getPackageFeatures(package_type).map((feature, index) => (
                                            <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    {feature.available ? (
                                                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                                    ) : (
                                                        <Cancel sx={{ fontSize: 18, color: 'error.main' }} />
                                                    )}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={feature.label}
                                                    primaryTypographyProps={{
                                                        variant: 'caption',
                                                        sx: {
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1.4,
                                                            color: feature.available ? 'text.primary' : 'text.disabled',
                                                            textDecoration: feature.available ? 'none' : 'line-through'
                                                        }
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>}
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        </Stack>
    );
};